import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { orderBy } from 'lodash/collection';

import { PatientDetailsApi } from '../../../../../../api';
import PatientInfoPageApi from '../../../../../../api/patient/PatientInfoPageApi';
import { onRequestError } from '../../../../../../services/handlers';
import { getPatientAgeFromDob, getPatientPreferredName } from '../../../../../../services/patient';
import { PageInfoHeader } from '../../../../../PageInfoHeader/PageInfoHeader';
import { generateUrlByKey, useCurrentRoute } from '../../../../../root/router';
import useScrollspy from '../../../shared/ReviewDEV/ReviewContent/useScrollspy';

import Container from './Container/Container';
import Sidebar from './Sidebar/Sidebar';
import useViewAllLogsState from './useViewAllLogsState/useViewAllLogsState';

import './ViewAllLogs.scss';

export default function ViewAllLogs() {
  const contentRefContainer = useRef(null);
  const { ssuPatientId } = useParams();
  const { params, parent } = useCurrentRoute();
  const [patientInfo, setPatientInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const { state, setAllLogs, updateItemInAllLogs } = useViewAllLogsState();
  const patientInfoKey = parent?.key;
  const { logs } = state;

  const ids = useMemo(
    function() {
      return logs.map(log => log.itemGroupTemplateId);
    },
    [logs]
  );

  const activeScrollId = useScrollspy(ids, contentRefContainer.current);

  const urlForPatientInfoPage = useMemo(
    function() {
      if (!patientInfoKey) {
        return null;
      }
      return generateUrlByKey(patientInfoKey, params);
    },
    [patientInfoKey, params]
  );

  useEffect(
    function() {
      PatientDetailsApi.getPatientDetails(ssuPatientId, true).then(function({ data: patientDetailsResponse }) {
        setPatientInfo(patientDetailsResponse);
      });
    },
    [ssuPatientId]
  );

  useEffect(
    function() {
      if (!ssuPatientId) {
        return;
      }
      setLoading(true);
      PatientInfoPageApi.getSitePatientLogs(ssuPatientId)
        .then(function(res) {
          setAllLogs(orderBy(res.data.logItemGroups, [log => log.name.toLowerCase()], ['asc']));
        }, onRequestError)
        .finally(function() {
          setLoading(false);
        });
    },
    [setAllLogs, ssuPatientId]
  );

  return (
    <>
      <PageInfoHeader
        objectRecordLabel={true}
        withLabel={false}
        pageInfo={
          <PageInfoHeader.CollapsibleList>
            <PageInfoHeader.AdditionalInfo tooltip="Patient Name">
              <NavLink to={urlForPatientInfoPage}>{patientInfo?.name + getPatientPreferredName(patientInfo)}</NavLink>
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo title="Patient ID">{patientInfo?.patientId}</PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo title="Subject ID">
              {patientInfo?.subjectId || 'No Subject ID'}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip="Date of Birth ">
              {patientInfo?.dob} ({getPatientAgeFromDob(patientInfo)})
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip="Study">{patientInfo?.studyName}</PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip="Site">{patientInfo?.siteName}</PageInfoHeader.AdditionalInfo>
          </PageInfoHeader.CollapsibleList>
        }
      />
      <div className="eds-view-all-logs">
        <Sidebar loading={loading} logs={logs} activeScrollId={activeScrollId} />
        <Container
          logs={logs}
          updateItemInAllLogs={updateItemInAllLogs}
          contentRefContainer={contentRefContainer}
          activeScrollId={activeScrollId}
        />
      </div>
    </>
  );
}
